import React, { Suspense, useEffect, useRef } from 'react'
import loadable from '@loadable/component'
import { get } from 'lodash'
import { Helmet } from 'react-helmet'

import { Navigate, Route, BrowserRouter, Routes, Outlet } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { settings } from '@krea/common/settings'
import { APPLICATION_NAME, COUNTRY_CODE } from '@krea/common/utils'
import { RouterNavigateOutsideComponents } from '@krea/common/history'

import { logout } from '@krea/common/store/user/actions'
import { Dialog } from '@krea/common/shared-components/dialog'
import { Preloader } from '@krea/common/shared-components/index'

import { fetchUserData } from 'store/user/actions'
import { fetchLoanApplicationsData } from 'store/loan-applications/actions'
import Layout from 'components/app/layout'
import HeaderSteps from 'components/app/header/HeaderSteps'
import ApplicationSelector from 'components/app/application-selector'
import { postFinnishAuth } from 'services/authService'
import { storeUtmParamsInCookie } from 'services/queryParams'

import SentryBoundary from './sentry'
import ScrollToTop from './ScrollToTop'

const PrivateRoute = loadable(() => import('./krea-direct/private-route'))
const DirectApp = loadable(() => import('./krea-direct/App'))
const LoginPage = loadable(() => import('@krea/common/shared-components/login'))
const NotFound = loadable(() => import('./pages/not-found'))
const Application = loadable(() => import('./pages/current-application'))
const DelegatedInformation = loadable(() => import('./pages/delegated-information'))

function CustomerWeb({ user, loading }) {
	const userId = useRef(0)
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const { appTitle, countryCode } = settings

	const useFetching = (someFetchActionCreator, condition = true) => {
		useEffect(() => {
			if (condition) {
				dispatch(someFetchActionCreator())
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [dispatch])
	}

	useFetching(fetchUserData)

	useEffect(() => {
		if (user.id && user.id !== userId.current) {
			dispatch(fetchLoanApplicationsData())
		} else {
			userId.current = user.id
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.id])

	useEffect(() => {
		storeUtmParamsInCookie()
	}, [])

	if (loading) {
		return <Preloader className="text-primary bg-white position-fixed w-100 h-100" size="xl" />
	}

	return (
		<SentryBoundary dispatch={dispatch} contactPersonId={user.id}>
			<BrowserRouter>
				<RouterNavigateOutsideComponents />

				<Layout
					MiddleNavbarComponent={HeaderSteps}
					user={user}
					logout={logout}
					applicationSelector={<ApplicationSelector />}
				>
					<Suspense fallback={null}>
						<Routes>
							<Route path="/" element={<Outlet />}>
								<Route index element={<Navigate replace={true} to="direct" />} />

								<Route path="redirect-return-page" element={<div />} />

								<Route
									path="/current-application/:id"
									element={
										<PrivateRoute>
											<Application />
										</PrivateRoute>
									}
								/>

								<Route
									path="loan-application-form"
									element={<Navigate replace={true} to="/direct/loan-application-form" />}
								/>

								<Route
									path="factoring-application-form"
									element={<Navigate replace={true} to="/direct/factoring-application-form" />}
								/>

								<Route
									path="preliminary-bid-form"
									element={
										<Navigate
											replace={true}
											to={countryCode === COUNTRY_CODE.SE ? '/direct/preliminary-bid-form' : '/'}
										/>
									}
								/>

								<Route
									path="real-estate-application-form"
									element={<Navigate replace={true} to="/direct/real-estate-application-form" />}
								/>

								<Route
									path="leasing-application-form"
									element={<Navigate replace={true} to="/direct/leasing-application-form" />}
								/>

								<Route
									path="improve-application-questions"
									element={<Navigate replace={true} to="/direct/improve-application-questions" />}
								/>

								<Route path="dashboard" element={<Navigate replace to="/direct/dashboard" />} />

								<Route
									path="selected-lender-response"
									element={<Navigate replace={true} to="/direct/selected-lender-response" />}
								/>

								<Route
									path="delegated-information"
									element={
										<>
											<Helmet title={`${appTitle} - Delegated Information`} />
											<DelegatedInformation />
										</>
									}
								/>

								<Route path="direct/*" element={<DirectApp />} />

								<Route
									path="login"
									element={
										!user?.id ? (
											<>
												<Helmet title={`${appTitle} - ${t('login.heading')}`} />
												<LoginPage appName={APPLICATION_NAME.CUSTOMER_WEB} postFinnishAuth={postFinnishAuth} />
											</>
										) : (
											<Navigate replace={true} to="/direct" />
										)
									}
								/>

								<Route path="*" element={<NotFound />} />
							</Route>
						</Routes>
					</Suspense>

					<ScrollToTop />

					<Dialog />
				</Layout>
			</BrowserRouter>
		</SentryBoundary>
	)
}

const mapStateToProps = (state) => ({
	user: get(state.userReducer, 'user', {}),
	loading: get(state.userReducer, 'loading')
})

export default connect(mapStateToProps)(CustomerWeb)
